/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 26 2024
 **/

import { useCallback } from "react";
import { atom, useAtomValue } from "jotai";
import { atomFamily, useAtomCallback } from "jotai/utils";

import { JotaiStore } from "@/frameworks/libraries/commons";

type SortType = "CREATED_DATE" | "NAME";

type SortStateType<T = SortType> = {
	sort: T;
	order: "DESC" | "ASC";
	active: boolean;
};

const sortByCreatedDateStateAtom = atom<SortStateType<"CREATED_DATE">>({
	sort: "CREATED_DATE",
	order: "DESC",
	active: true
});

const sortByNameStateAtom = atom<SortStateType<"NAME">>({
	sort: "NAME",
	order: "ASC",
	active: false
});

const activeSortStateAtom = atom((get) => {
	const sortByCreatedDateState = get(sortByCreatedDateStateAtom);
	const sortByNameState = get(sortByNameStateAtom);

	if (sortByCreatedDateState.active) {
		return sortByCreatedDateState;
	}

	return sortByNameState;
});

const sortStateAtomFamily = atomFamily((arg: SortType) => {
	if (arg === "CREATED_DATE") return sortByCreatedDateStateAtom;
	return sortByNameStateAtom;
});

export default function useFileSortedState() {
	const getState = useAtomCallback(
		useCallback((get, _, arg: SortType) => {
			if (arg === "NAME") return get(sortByNameStateAtom);
			return get(sortByCreatedDateStateAtom);
		}, []),
		{ store: JotaiStore }
	);

	const getCurrentState = useAtomCallback(
		useCallback((get) => {
			return get(activeSortStateAtom);
		}, []),
		{ store: JotaiStore }
	);

	const inactiveSortState = useAtomCallback(
		useCallback((_, set, arg: SortType) => {
			if (arg === "CREATED_DATE") {
				set(sortByCreatedDateStateAtom, (prev) => {
					return { ...prev, active: false };
				});
			}
			if (arg === "NAME") {
				set(sortByNameStateAtom, (prev) => {
					return { ...prev, active: false };
				});
			}
		}, []),
		{ store: JotaiStore }
	);

	const activeSortState = useAtomCallback(
		useCallback((_, set, arg: SortType) => {
			if (arg === "CREATED_DATE") {
				set(sortByCreatedDateStateAtom, (prev) => {
					return { ...prev, active: true };
				});
			}
			if (arg === "NAME") {
				set(sortByNameStateAtom, (prev) => {
					return { ...prev, active: true };
				});
			}
		}, []),
		{ store: JotaiStore }
	);

	const changeSort = useAtomCallback(
		useCallback((get) => {
			const sortByCreatedDateState = get(sortByCreatedDateStateAtom);
			const sortByNameState = get(sortByNameStateAtom);
			if (sortByCreatedDateState.active) {
				inactiveSortState("CREATED_DATE");
				activeSortState("NAME");
			}
			if (sortByNameState.active) {
				inactiveSortState("NAME");
				activeSortState("CREATED_DATE");
			}
		}, []),
		{ store: JotaiStore }
	);

	const changeOrder = useAtomCallback(
		useCallback((get, set) => {
			const sortByCreatedDateState = get(sortByCreatedDateStateAtom);
			const sortByNameState = get(sortByNameStateAtom);
			if (sortByCreatedDateState.active) {
				set(sortByCreatedDateStateAtom, (prev) => {
					const orderTo = prev.order === "DESC" ? "ASC" : "DESC";
					return { ...prev, order: orderTo };
				});
			}
			if (sortByNameState.active) {
				set(sortByNameStateAtom, (prev) => {
					const orderTo = prev.order === "DESC" ? "ASC" : "DESC";
					return { ...prev, order: orderTo };
				});
			}
		}, []),
		{ store: JotaiStore }
	);

	const useRenderActiveSortState = () => {
		return useAtomValue(activeSortStateAtom);
	};

	const useRenderSortState = (arg: SortType) => {
		return useAtomValue(sortStateAtomFamily(arg));
	};

	const sortFileListActions = {
		getState,
		getCurrentState,
		changeOrder,
		changeSort
	};

	return { sortFileListActions, useRenderActiveSortState, useRenderSortState };
}
